import * as React from "react";
import TextField from "@mui/material/TextField";
import type { TextFieldProps } from "@mui/material/TextField";
import { cn } from "../../lib/utils";

type NativeInputHtml = Pick<
  React.InputHTMLAttributes<HTMLInputElement>,
  "maxLength" | "minLength" | "autoComplete" | "pattern" | "inputMode" | "readOnly"
>;

export interface InputProps
  extends Omit<TextFieldProps, "variant" | "error" | "helperText" | "label" | keyof NativeInputHtml>,
    NativeInputHtml {
  error?: string;
  label?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type = "text",
      error,
      label,
      id,
      sx,
      maxLength,
      minLength,
      autoComplete,
      pattern,
      inputMode,
      readOnly,
      inputProps,
      ...props
    },
    ref
  ) => {
    const autoId = React.useId();
    const inputId = id ?? autoId;

    return (
      <TextField
        id={inputId}
        label={label}
        type={type}
        error={Boolean(error)}
        helperText={error}
        fullWidth
        variant="outlined"
        inputRef={ref}
        className={cn(className)}
        inputProps={{
          maxLength,
          minLength,
          autoComplete,
          pattern,
          inputMode,
          readOnly,
          ...inputProps,
        }}
        FormHelperTextProps={{ className: "font-medium" }}
        InputLabelProps={
          label
            ? {
                shrink: true,
                sx: { fontWeight: 500, color: "#334155", fontSize: "0.875rem", mb: 0.5 },
              }
            : undefined
        }
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "12px",
            backgroundColor: "rgba(239, 246, 255, 0.7)",
            fontWeight: 600,
            fontSize: "0.875rem",
            "& fieldset": { borderColor: "#bfdbfe" },
            "&:hover fieldset": { borderColor: "#93c5fd" },
            "&.Mui-focused fieldset": {
              borderColor: "#2563eb",
              borderWidth: "1px",
              boxShadow: "0 0 0 4px rgba(37, 99, 235, 0.12)",
            },
          },
          "& .MuiOutlinedInput-input": {
            py: 1.5,
            px: 2,
            color: "#0f172a",
          },
          "& .MuiOutlinedInput-input::placeholder": {
            fontWeight: 400,
            opacity: 1,
            color: "#94a3b8",
          },
          "& .MuiFormHelperText-root": { marginLeft: 0 },
          ...(sx as object),
        }}
        {...props}
      />
    );
  }
);
Input.displayName = "Input";

export { Input };
